/**
 * 랜딩 페이지 - FSD pages 레이어
 * 실제 랜딩 페이지 라우트 구현체
 */ import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { LandingPageTemplate } from '@edusense/widgets-landing-page-template';
import { Button } from '@edusense/shared-ui-kit';
import { LANDING_PAGE_TEST_IDS } from '@edusense/shared-constants';
import { useLandingPage } from './hooks/useLandingPage';
import * as m from "@paraglide/messages";
import { LanguageService } from '@edusense/shared-i18n';
/**
 * 메인 랜딩 페이지 컴포넌트
 * 
 * @example
 * ```tsx
 * <LandingPage />
 * ```
 */ export var LandingPage = function(param) {
    var content = param.content, ctaText = param.ctaText, loginText = param.loginText, loadingText = param.loadingText, className = param.className, heroActions = param.heroActions, headerActions = param.headerActions, propLoginClick = param.onLoginClick, propStartClick = param.onStartClick;
    var navigate = useNavigate();
    var params = useParams();
    var currentLanguage = React.useMemo(function() {
        if ((params === null || params === void 0 ? void 0 : params.lang) && /^[a-z]{2}$/.test(params.lang)) {
            return params.lang;
        }
        if (typeof window !== 'undefined') {
            var extracted = LanguageService.extractLanguageFromUrl(window.location.pathname);
            if (extracted) {
                return extracted;
            }
        }
        return LanguageService.getDefaultLanguage();
    }, [
        params === null || params === void 0 ? void 0 : params.lang
    ]);
    var safeLanguage = /^[a-z]{2}$/.test(currentLanguage) ? currentLanguage : 'ko';
    // i18n 기반 기본 콘텐츠 (fallback)
    var defaultContent = {
        hero: {
            title: m.landing_heroTitle(),
            gradientText: 'EduSense',
            titleSuffix: m.landing_heroTitleSuffix(),
            description: m.landing_heroDescription()
        },
        features: {
            title: m.landing_featuresTitle(),
            items: [
                {
                    icon: '🤖',
                    title: m.landing_features_aiCoding_title(),
                    description: m.landing_features_aiCoding_description()
                },
                {
                    icon: '📊',
                    title: m.landing_features_aiTutor_title(),
                    description: m.landing_features_aiTutor_description()
                },
                {
                    icon: '💬',
                    title: m.landing_features_socialLearning_title(),
                    description: m.landing_features_socialLearning_description()
                }
            ]
        },
        footer: {
            copyright: m.landing_footer_copyright()
        }
    };
    // props로 받은 content 사용하거나 기본값 사용
    var landingPageContent = content || defaultContent;
    var computedCtaText = ctaText !== null && ctaText !== void 0 ? ctaText : m.landing_startLearning();
    var computedLoginText = loginText !== null && loginText !== void 0 ? loginText : m.common_login();
    var computedLoadingText = loadingText !== null && loadingText !== void 0 ? loadingText : m.common_loading();
    var _useLandingPage = useLandingPage({
        enableAutoScroll: true,
        enableAnalytics: true
    }), hookStartClick = _useLandingPage.handleStartClick, hookLoginClick = _useLandingPage.handleLoginClick, scrollToSection = _useLandingPage.scrollToSection, isLoading = _useLandingPage.isLoading;
    // 🚀 공통 함수 우선 사용: props로 받은 핸들러가 있으면 사용, 없으면 기본 훅 사용
    var handleStartClick = propStartClick || hookStartClick;
    var handleLoginClick = propLoginClick || hookLoginClick;
    var handlePricingClick = React.useCallback(function() {
        navigate("/".concat(safeLanguage, "/pricing"));
    }, [
        navigate,
        safeLanguage
    ]);
    // 기본 Hero 액션 버튼들
    var defaultHeroActions = /*#__PURE__*/ React.createElement("div", {
        className: "flex gap-4"
    }, /*#__PURE__*/ React.createElement(Button, {
        size: "lg",
        onClick: handleStartClick,
        disabled: isLoading,
        className: "min-w-[160px]",
        "data-testid": LANDING_PAGE_TEST_IDS.START_LEARNING_BUTTON
    }, isLoading ? computedLoadingText : computedCtaText));
    // 기본 헤더 액션 버튼들
    var defaultHeaderActions = /*#__PURE__*/ React.createElement(Button, {
        variant: "secondary",
        size: "sm",
        onClick: handleLoginClick,
        disabled: isLoading,
        "data-testid": LANDING_PAGE_TEST_IDS.LOGIN_BUTTON
    }, computedLoginText);
    return /*#__PURE__*/ React.createElement(LandingPageTemplate, {
        content: landingPageContent,
        className: className,
        header: {
            logoText: m.common_appName(),
            onLogoClick: function() {
                return navigate('/feed');
            },
            showPricing: true,
            showLanguageSelector: true,
            rightActions: headerActions || defaultHeaderActions,
            onPricingClick: handlePricingClick
        },
        hero: {
            actions: heroActions || defaultHeroActions
        }
    });
};
export default LandingPage;
